import dayjs from '@/helpers/dayjs';
import type { PickupTime, Warehouse } from '@/warehouse';
import DeliveryOptions from '@/helpers/deliveryoptions';

export type PickupTimesOptions = {
  includeClosed?: boolean;
  includeNextOpenDay?: boolean;
};

export class WarehouseCalendar {
  private readonly source: PickupTime[];
  public readonly nextOpenDay: string;
  public readonly earliestPickup: string;

  constructor(
    input:
      | Pick<Warehouse, 'pickuptimes' | 'nextOpenDay' | 'earliestPickup'>
      | { pickuptimes: PickupTime[]; nextOpenDay?: string; earliestPickup?: string }
  ) {
    this.source = [...(input.pickuptimes ?? [])];
    this.nextOpenDay = input.nextOpenDay ?? '';
    this.earliestPickup = input.earliestPickup ?? '';
  }

  pickupTimes(options: PickupTimesOptions = {}): PickupTime[] {
    const { includeClosed = false, includeNextOpenDay = false } = options;
    const result = [...this.source];

    if (includeNextOpenDay && this.nextOpenDay && result[0]?.date !== this.nextOpenDay) {
      result.unshift({ date: this.nextOpenDay, slots: [] });
    }

    return includeClosed ? result : result.filter((p) => p.type !== 'closed');
  }

  isClosed(date: Date | string | dayjs.Dayjs): boolean {
    const d = dayjs(date);
    const item = this.pickupTimes({ includeClosed: true, includeNextOpenDay: true }).find((p) =>
      d.isSame(p.date, 'day')
    );
    return item?.type === 'closed';
  }

  isDeliveryDateAvailable({
    deliveryTerms,
    date,
    isM3Order,
  }: {
    date: Date | string | dayjs.Dayjs;
    deliveryTerms?: string;
    isM3Order?: boolean;
  }): boolean {
    const pickuptimes = this.pickupTimes({ includeClosed: true, includeNextOpenDay: true });
    if (!pickuptimes?.length) return true; // Available

    const d1 = dayjs(date);
    const indexOfDay = pickuptimes.findIndex((item) => {
      return d1.isSame(item.date, 'day');
    });

    if (indexOfDay < 0 || indexOfDay + 1 >= pickuptimes.length) {
      return false; // Unavailable
    }

    if (pickuptimes[indexOfDay].type === 'closed') {
      return false; // Unavailable
    }

    try {
      const deliveryOption = DeliveryOptions.getByTerms(deliveryTerms ?? '');
      let eligibleDays = pickuptimes.slice(0, indexOfDay + 1);
      const leadtimeOption = dayjs().hour() < (deliveryOption.getBreakPoint(isM3Order)?.hour() ?? 11) ? 0 : 1;
      const leadtime = deliveryOption.leadTime?.length ? deliveryOption.leadTime : ['1', '2'];
      const hasClosedDays = eligibleDays.some((p) => p.type === 'closed');

      if (hasClosedDays) {
        eligibleDays = eligibleDays.filter((p) => p.type !== 'closed');
        return eligibleDays.length >= Number(leadtime[leadtimeOption]);
      }
    } catch (e) {
      if (deliveryTerms) console.warn(e);
    }

    return true; // Available
  }
}
